<template>
  <PrimeDatePicker
    v-model="parsedDate"
    class="w-full"
    @change="changeEventHandler($event)"
    @blur="blurEventHandler($event)"
    @input="inputEventHandler($event)"
    v-bind="$attrs"
    :disabled="loading || disabled"
    :showIcon="true"
    :timeOnly="true"
    hourFormat="24"
    :invalid="hasErrors"
    :id="props.id"
    :placeholder="props.placeholder"
  >
  </PrimeDatePicker>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import {
  type IInputEvent,
  type IChangeEvent,
  type IBlurEvent,
} from "~/scad-shared/types/interfaces";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    inputType?: string;
    error?: string;
    loading?: boolean;
    id?: string;
    placeholder?: string;
  }>(),
  {
    disabled: false,
    loading: false,
  }
);

const value = defineModel<any>("value");

const hasErrors: ComputedRef<boolean> = computed(() => {
  if (props.error && props.error !== ""){
    return true;
  }
  return false;
})

const emits = defineEmits<{
  change: [event: IInputEvent];
  input: [event: IChangeEvent];
  blur: [event: IBlurEvent];
}>();


// Computed property to handle date parsing
const parsedDate = computed({
  get() {
    //console.log('getting value.value', value.value, dayjs(value.value).isValid())
    if (!value.value) return undefined;

    const timePattern12h = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM|am|pm)$/;
    const timePattern24h = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    // Check if the timeString matches the 12-hour pattern
    if (timePattern12h.test(value.value)) {
      const datetimeString = `2024-01-01 ${value.value}`; // Use today's date or any valid date
      // Parse the datetime string
      return dayjs(datetimeString, "YYYY-MM-DD hh:mm a").toDate();
    }

    // Check if the timeString matches the 24-hour pattern (H:mm or HH:mm)
    if (timePattern24h.test(value.value)) {
      const datetimeString = `2024-01-01 ${value.value}`; // Use today's date or any valid date
      // Parse the datetime string
      return dayjs(datetimeString, "YYYY-MM-DD H:mm").toDate();
    }

    return dayjs(value.value).isValid() ? dayjs(value.value).toDate() : undefined;
  },
  set(newDate) {
    //console.log('setting value.value', newDate, `with type: ${typeof(newDate)}`)
    value.value = newDate ? dayjs(newDate).format('H:mm') : null;
  },
});

function formatDate(date: any) {
  return dayjs(date).format("H:mm").trim();
}

function changeEventHandler($event: any) {
  emits("change", { ...$event, value: formatDate(value.value) });
}
function inputEventHandler($event: any) {
  emits("input", { ...$event, value: formatDate(value.value) });
}
function blurEventHandler($event: any) {
  emits("blur", { ...$event, value: formatDate(value.value) });
}
</script>

<style scoped></style>
